import React, { useEffect, useMemo, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios"; import { handleAuthError } from "../../utils";
import withAuth from "../withAuth";
import {
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Spinner,
  Table,
  Button,
  Input,
  FormGroup,
  Label,
  Form,
  FormFeedback,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from '../../Network';
import PaginationControls from "../../components/Common/PaginationControls";

const emptyAdminForm = {
  fullName: '',
  email: '',
  password: '',
  displayName: '',
  role: 1,
  isSuperAdmin: false,
  status: true,
};

const AdminManagement = () => {
  document.title = "Admin Management | RYD Admin";

  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ ...emptyAdminForm });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusBusyId, setStatusBusyId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [showAll, setShowAll] = useState(false);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("authUser") || "null");
    } catch {
      return null;
    }
  }, []);
  const isCurrentSuperAdmin = !!currentUser?.isSuperAdmin;

  useEffect(() => {
    fetchAdmins();
  }, [currentPage, pageSize, search]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const limit = showAll ? 10000 : pageSize;
      const params = {
        page: currentPage,
        limit: limit,
        ...(search && { search })
      };
      const response = await axios.get(`${baseUrl}/admin/all`, { params });
      const responseData = response.data.data;
      const data = Array.isArray(responseData) ? responseData : (responseData?.data || []);
      const paginationData = responseData?.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: Array.isArray(data) ? data.length : 0,
        itemsPerPage: limit,
        hasNextPage: false,
        hasPreviousPage: false
      };
      setAdmins(Array.isArray(data) ? data : []);
      setPagination(paginationData);
      setLoading(false);
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      toast.error("Failed to fetch admins");
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    if (size === 'all') {
      setShowAll(true);
      setPageSize(10000);
    } else {
      setShowAll(false);
      setPageSize(parseInt(size));
      setCurrentPage(1);
    }
  };

  const goToNextPage = () => {
    if (pagination.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (pagination.hasPreviousPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setCurrentPage(1);
  };

  const toggleCreateModal = () => {
    setCreateModal(!createModal);
    setValidationErrors({});
    setNewAdmin({ ...emptyAdminForm });
    setConfirmPassword('');
  };

  const toggleEditModal = (admin = null) => {
    if (admin) {
      setCurrentAdmin(admin);
      setNewAdmin({
        fullName: admin.fullName,
        email: admin.email,
        password: '',
        displayName: admin.displayName,
        role: admin.role,
        isSuperAdmin: !!admin.isSuperAdmin,
        status: admin.status !== false,
      });
    }
    setEditModal(!editModal);
    setValidationErrors({});
    setConfirmPassword('');
  };

  const toggleDeleteModal = (admin = null) => {
    setCurrentAdmin(admin);
    setDeleteModal(!deleteModal);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAdmin(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const validateForm = (isEdit = false) => {
    const errors = {};

    if (!newAdmin.fullName.trim()) errors.fullName = 'Full name is required';
    if (!newAdmin.email.trim()) errors.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(newAdmin.email)) errors.email = 'Email is invalid';
    
    if (!isEdit) {
      if (!newAdmin.password) errors.password = 'Password is required';
      else if (newAdmin.password.length < 6) errors.password = 'Password must be at least 6 characters';
      if (newAdmin.password !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    } else if (newAdmin.password) {
      if (newAdmin.password.length < 6) errors.password = 'Password must be at least 6 characters';
      if (newAdmin.password !== confirmPassword) errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;
    
    const payload = {
      ...newAdmin,
      role: parseInt(newAdmin.role, 10),
      isSuperAdmin: isCurrentSuperAdmin ? !!newAdmin.isSuperAdmin : false,
      status: true,
    };

    try {
      setIsSubmitting(true);
      await axios.post(`${baseUrl}/admin/auth/create`, payload);
      toast.success('Admin created successfully');
      fetchAdmins();
      toggleCreateModal();
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to create admin");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();

    if (!validateForm(true)) return;
    
    const updateData = {
      fullName: newAdmin.fullName,
      email: newAdmin.email,
      displayName: newAdmin.displayName,
      role: parseInt(newAdmin.role, 10),
    };

    if (newAdmin.password) {
      updateData.password = newAdmin.password;
    }
    if (isCurrentSuperAdmin) {
      updateData.isSuperAdmin = !!newAdmin.isSuperAdmin;
      updateData.status = !!newAdmin.status;
    }

    try {
      setIsSubmitting(true);
      await axios.put(
        `${baseUrl}/admin/${currentAdmin.id}`,
        updateData
      );
      toast.success('Admin updated successfully');
      fetchAdmins();
      toggleEditModal();
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update admin");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleStatus = async (admin) => {
    if (!isCurrentSuperAdmin) {
      toast.error("Only super admins can enable or disable admins");
      return;
    }
    if (Number(currentUser?.id) === Number(admin.id)) {
      toast.error("You cannot disable your own account");
      return;
    }
    const nextStatus = admin.status === false;
    try {
      setStatusBusyId(admin.id);
      await axios.put(`${baseUrl}/admin/${admin.id}/status`, { status: nextStatus });
      toast.success(nextStatus ? "Admin enabled" : "Admin disabled");
      fetchAdmins();
    } catch (error) {
      handleAuthError(error);
      toast.error(error.response?.data?.message || "Failed to update admin status");
    } finally {
      setStatusBusyId(null);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!isCurrentSuperAdmin) {
      toast.error("Only super admins can delete admins");
      return;
    }
    try {
      setIsSubmitting(true);
      await axios.delete(
        `${baseUrl}/admin/${currentAdmin.id}`
      );
      toast.success('Admin deleted successfully');
      fetchAdmins();
      toggleDeleteModal();
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete admin");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 0:
        return <Badge color="secondary">Viewer</Badge>;
      case 1:
        return <Badge color="primary">Admin</Badge>;
      case 2:
        return <Badge color="info">Sub-Admin</Badge>;
      case 3:
        return <Badge color="warning">Marketer</Badge>;
      default:
        return <Badge color="light">Unknown</Badge>;
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Admin Management" />
          <Row>
            <Col lg="12">
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="mb-3">
                    <h5 className="card-title">
                      Admin List{" "}
                      <span className="text-muted fw-normal ms-2">
                        ({pagination.totalItems})
                      </span>
                    </h5>
                    {!isCurrentSuperAdmin ? (
                      <p className="text-muted mb-0 small">
                        Only a super admin can create, edit, enable/disable, or delete admins.
                      </p>
                    ) : null}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                    <div>
                      <Input
                        type="text"
                        placeholder="Search admins..."
                        value={search}
                        onChange={handleSearch}
                        className="me-2"
                        style={{ width: '200px' }}
                      />
                    </div>
                    <div>
                      <button
                        className="btn btn-primary"
                        onClick={fetchAdmins}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sync-alt me-2"></i>
                            Refresh
                          </>
                        )}
                      </button>
                      {isCurrentSuperAdmin ? (
                        <button
                          className="btn btn-success ms-2"
                          onClick={toggleCreateModal}
                        >
                          <i className="fas fa-plus me-2"></i>
                          Create Admin
                        </button>
                      ) : null}
                    </div>
                  </div>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center mt-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive mt-4 shadow rounded" style={{ backgroundColor: '#f9f9fb' }}>
                  <Table className="table align-middle table-hover">
                    <thead className="bg-primary text-white" style={{ borderRadius: '12px' }}>
                      <tr>
                        <th>#</th>
                        <th>Full Name</th>
                        <th>Display Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Super Admin</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map((admin, index) => {
                        const globalIndex = showAll 
                          ? index 
                          : (pagination.currentPage - 1) * pagination.itemsPerPage + index;
                        const isActive = admin.status !== false;
                        return (
                          <tr
                            key={admin.id || index}
                            className={`align-middle ${globalIndex % 2 === 0 ? 'bg-white' : 'bg-light'}`}
                            style={{ borderBottom: '1px solid #e0e0e0' }}
                          >
                            <td>{globalIndex + 1}</td>
                            <td>{admin.fullName}</td>
                            <td>{admin.displayName}</td>
                            <td>{admin.email}</td>
                            <td>{getRoleBadge(admin.role)}</td>
                            <td>
                              {admin.isSuperAdmin ? (
                                <Badge color="danger">Yes</Badge>
                              ) : (
                                <Badge color="light" className="text-dark">No</Badge>
                              )}
                            </td>
                            <td>
                              {isActive ? (
                                <Badge color="success">Active</Badge>
                              ) : (
                                <Badge color="secondary">Disabled</Badge>
                              )}
                            </td>
                            <td>{new Date(admin.createdAt).toLocaleString()}</td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                {isCurrentSuperAdmin ? (
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => toggleEditModal(admin)}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </Button>
                                ) : null}
                                {isCurrentSuperAdmin && Number(currentUser?.id) !== Number(admin.id) ? (
                                  <Button
                                    color={isActive ? "warning" : "success"}
                                    size="sm"
                                    disabled={statusBusyId === admin.id}
                                    onClick={() => handleToggleStatus(admin)}
                                    title={isActive ? "Disable admin" : "Enable admin"}
                                  >
                                    {statusBusyId === admin.id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <i className={`fas ${isActive ? "fa-user-slash" : "fa-user-check"}`}></i>
                                    )}
                                  </Button>
                                ) : null}
                                {isCurrentSuperAdmin && Number(currentUser?.id) !== Number(admin.id) ? (
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => toggleDeleteModal(admin)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
              {!loading && admins.length > 0 && (
                <PaginationControls
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  pageSize={pagination.itemsPerPage}
                  showAll={showAll}
                  handlePageChange={handlePageChange}
                  handlePageSizeChange={handlePageSizeChange}
                  goToNextPage={goToNextPage}
                  goToPreviousPage={goToPreviousPage}
                  canPreviousPage={pagination.hasPreviousPage}
                  canNextPage={pagination.hasNextPage}
                  paginatedDataLength={admins.length}
                />
              )}
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={createModal} toggle={toggleCreateModal}>
        <ModalHeader toggle={toggleCreateModal}>Create New Admin</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleCreateAdmin}>
            <FormGroup>
              <Label>Full Name</Label>
              <Input
                type="text"
                name="fullName"
                value={newAdmin.fullName}
                onChange={handleInputChange}
                invalid={!!validationErrors.fullName}
              />
              {validationErrors.fullName && (
                <FormFeedback>{validationErrors.fullName}</FormFeedback>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Display Name</Label>
              <Input
                type="text"
                name="displayName"
                value={newAdmin.displayName}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                name="email"
                value={newAdmin.email}
                onChange={handleInputChange}
                invalid={!!validationErrors.email}
              />
              {validationErrors.email && (
                <FormFeedback>{validationErrors.email}</FormFeedback>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Password</Label>
              <Input
                type="password"
                name="password"
                value={newAdmin.password}
                onChange={handleInputChange}
                invalid={!!validationErrors.password}
              />
              {validationErrors.password && (
                <FormFeedback>{validationErrors.password}</FormFeedback>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                invalid={!!validationErrors.confirmPassword}
              />
              {validationErrors.confirmPassword && (
                <FormFeedback>{validationErrors.confirmPassword}</FormFeedback>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Role</Label>
              <Input
                type="select"
                name="role"
                value={newAdmin.role}
                onChange={handleInputChange}
              >
                <option value={0}>Viewer</option>
                <option value={1}>Admin</option>
                <option value={2}>Sub-Admin</option>
                <option value={3}>Marketer</option>
              </Input>
            </FormGroup>

            {isCurrentSuperAdmin ? (
              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  name="isSuperAdmin"
                  id="createIsSuperAdmin"
                  checked={!!newAdmin.isSuperAdmin}
                  onChange={handleInputChange}
                />
                <Label check htmlFor="createIsSuperAdmin">
                  Super Admin (can enable/disable admins)
                </Label>
              </FormGroup>
            ) : null}

            <div className="mt-3 d-flex justify-content-end">
              <Button color="secondary" onClick={toggleCreateModal} className="me-2">
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create Admin'
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <Modal isOpen={editModal} toggle={toggleEditModal}>
        <ModalHeader toggle={toggleEditModal}>Edit Admin</ModalHeader>
        <ModalBody>
          <Form onSubmit={handleUpdateAdmin}>
            <FormGroup>
              <Label>Full Name</Label>
              <Input
                type="text"
                name="fullName"
                value={newAdmin.fullName}
                onChange={handleInputChange}
                invalid={!!validationErrors.fullName}
              />
              {validationErrors.fullName && (
                <FormFeedback>{validationErrors.fullName}</FormFeedback>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Display Name</Label>
              <Input
                type="text"
                name="displayName"
                value={newAdmin.displayName}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                name="email"
                value={newAdmin.email}
                onChange={handleInputChange}
                invalid={!!validationErrors.email}
              />
              {validationErrors.email && (
                <FormFeedback>{validationErrors.email}</FormFeedback>
              )}
            </FormGroup>

            <FormGroup>
              <Label>Password (leave blank to keep current)</Label>
              <Input
                type="password"
                name="password"
                value={newAdmin.password}
                onChange={handleInputChange}
                invalid={!!validationErrors.password}
              />
              {validationErrors.password && (
                <FormFeedback>{validationErrors.password}</FormFeedback>
              )}
            </FormGroup>

            {newAdmin.password && (
              <FormGroup>
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  invalid={!!validationErrors.confirmPassword}
                />
                {validationErrors.confirmPassword && (
                  <FormFeedback>{validationErrors.confirmPassword}</FormFeedback>
                )}
              </FormGroup>
            )}

            <FormGroup>
              <Label>Role</Label>
              <Input
                type="select"
                name="role"
                value={newAdmin.role}
                onChange={handleInputChange}
              >
                <option value={0}>Viewer</option>
                <option value={1}>Admin</option>
                <option value={2}>Sub-Admin</option>
                <option value={3}>Marketer</option>
              </Input>
            </FormGroup>

            {isCurrentSuperAdmin ? (
              <>
                <FormGroup check className="mb-2">
                  <Input
                    type="checkbox"
                    name="isSuperAdmin"
                    id="editIsSuperAdmin"
                    checked={!!newAdmin.isSuperAdmin}
                    onChange={handleInputChange}
                  />
                  <Label check htmlFor="editIsSuperAdmin">
                    Super Admin
                  </Label>
                </FormGroup>
                <FormGroup check className="mb-3">
                  <Input
                    type="checkbox"
                    name="status"
                    id="editStatus"
                    checked={!!newAdmin.status}
                    onChange={handleInputChange}
                    disabled={Number(currentUser?.id) === Number(currentAdmin?.id)}
                  />
                  <Label check htmlFor="editStatus">
                    Active (enabled)
                  </Label>
                </FormGroup>
              </>
            ) : null}

            <div className="mt-3 d-flex justify-content-end">
              <Button color="secondary" onClick={toggleEditModal} className="me-2">
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Update Admin'
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete admin <strong>{currentAdmin?.fullName}</strong>?</p>
          <p>This action cannot be undone.</p>
          <div className="d-flex justify-content-end mt-3">
            <Button color="secondary" onClick={toggleDeleteModal} className="me-2">
              Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteAdmin} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                'Delete Admin'
              )}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(AdminManagement);
